import type { MaybeRefOrGetter } from 'vue';
import { computed, toValue, watchEffect } from 'vue';
import { useBlockInEditorContext } from '#layers/block-engine/utils/block/api/context';

/**
 * Applies the interactive-zone marker to an element while `active` is true.
 */
export function useBlockInteractiveZoneElement(
	el: MaybeRefOrGetter<HTMLElement | null | undefined>,
	active: MaybeRefOrGetter<boolean>,
) {
	const isActive = computed(() => toValue(active));

	watchEffect((onCleanup) => {
		const node = toValue(el);

		if (!node || !isActive.value) {
			return;
		}

		node.dataset.blockEditorInteractiveZone = '';

		onCleanup(() => {
			delete node.dataset.blockEditorInteractiveZone;
		});
	});

	return {
		active: isActive,
	};
}

/**
 * Marks an element as an interactive block region while it is rendered in the block editor.
 *
 * Overlay handlers use the marker to distinguish interactive block content from builder chrome.
 */
export function useBlockInteractiveZone(
	el: MaybeRefOrGetter<HTMLElement | null | undefined>,
	disabled: MaybeRefOrGetter<boolean> = false,
) {
	const { blockInEditor, isPreview } = useBlockInEditorContext();

	return useBlockInteractiveZoneElement(el, () => blockInEditor && !isPreview && !toValue(disabled));
}
